import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { IDLE_TIME } from './../../_config/config';
import { AuthService, UIService } from './../../_services/index';
import { CountUpdateService } from '../../_services/count-update.service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { createIdleTimeoutManager, sessionWarningMessage, shouldRefreshToken, watchActivity } from '../../_utils/idle-timeout';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  username: string;
  isOpen = false;

  logo: string;
  logoSubjectListener: Subscription;

  /** Sign-out countdown toast: the sentence read out once, and the text that counts down */
  warningAnnouncement: string;
  warningText: string;
  private warningCountdown: ReturnType<typeof setInterval>;

  private idleManager: ReturnType<typeof createIdleTimeoutManager>;
  private stopTokenRefresh: () => void;
  private refreshing = false;

  constructor(
    public authService: AuthService,
    public countUpdate : CountUpdateService,
    private UIService: UIService,
    private router: Router,
    private zone: NgZone) {
  }

  ngOnInit() {
    this.isOpen = false;
    this.watchSession();

    this.logoSubjectListener = this.UIService.logoSubject.subscribe(logo => {
      this.logo = logo;
    });
  }

  ngOnDestroy() {
    this.logoSubjectListener.unsubscribe();
    this.idleManager.stop();
    this.stopTokenRefresh();
    clearInterval(this.warningCountdown);
  }

  changeName(name:any){
    this.username= name;
  }

  openDropdown() {
    this.isOpen = !this.isOpen;
  }
  logout(){
    this.idleManager.stop();
    this.router.navigate(['/logout']);
  }

  /** Sign out after IDLE_TIME without activity, warning a minute ahead */
  private watchSession() {
    // activity listeners run outside Angular so mouse moves don't trigger change detection
    this.zone.runOutsideAngular(() => {
      this.idleManager = createIdleTimeoutManager({
        target: document,
        onIdle: () => this.zone.run(() => this.signOut(`You have been idle for ${IDLE_TIME/60} minutes. Please login again`)),
        onWarning: msLeft => this.zone.run(() => this.showWarning(msLeft)),
        onWarningCleared: () => this.zone.run(() => this.hideWarning()),
      });
      this.idleManager.start();

      // keep the access token fresh while the user is active, 5 s after activity settles
      this.stopTokenRefresh = watchActivity({ target: document, delayMs: 5000, onSettled: () => this.refreshToken() });
    });
  }

  private refreshToken() {
    if (this.refreshing || !shouldRefreshToken(this.authService.user)) { return; }

    this.refreshing = true;
    this.authService.refreshAccessToken()
      .pipe(finalize(() => this.refreshing = false))
      .subscribe({
        next: ({ access_token }) => this.authService.setAccessToken(access_token),
        error: () => {}, // the idle timer still signs out; a failed refresh changes nothing
      });
  }

  private showWarning(msLeft: number) {
    const endsAt = Date.now() + msLeft;
    const update = () => this.warningText = sessionWarningMessage(endsAt - Date.now());

    clearInterval(this.warningCountdown);
    this.warningAnnouncement = sessionWarningMessage(msLeft);
    update();
    this.warningCountdown = setInterval(update, 1000);
  }

  private hideWarning() {
    clearInterval(this.warningCountdown);
    this.warningAnnouncement = this.warningText = undefined;
  }

  private signOut(message: string) {
    this.hideWarning();
    this.authService.logout()
      .pipe(finalize(() => this.router.navigate(['/login'], { state: { message } })))
      .subscribe({ error: () => {} }); // signed out locally either way
  }

  removeBack(){
    this.countUpdate.removeBack.next(false);
  }
}
